import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const SidebarCategories = ({ isOpen, onClose, categories = [] }) => {
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [expandedSubCategories, setExpandedSubCategories] = useState([]);
  const navigate = useNavigate(); // ✅ Usamos el hook de React Router

  const handleCategoryClick = (category) => {
    setExpandedCategories((prev) =>
      prev.includes(category.id)
        ? prev.filter((id) => id !== category.id)
        : [...prev, category.id]
    );
  };

  const handleSubCategoryClick = (subCategory, categorySlug) => {
    setExpandedSubCategories((prev) =>
      prev.includes(subCategory.id)
        ? prev.filter((id) => id !== subCategory.id)
        : [...prev, subCategory.id]
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80]"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 w-[300px] sm:w-[380px] h-full bg-white shadow-2xl z-[90] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
              <h5 className="text-lg font-bold mb-0 text-brand-blue">Categorías</h5>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500"
              >
                <FontAwesomeIcon icon={faTimes} className="text-lg" />
              </button>
            </div>

            {/* Categories List */}
            <div className="flex-1 overflow-y-auto py-3 px-3">
              <ul className="space-y-1 pl-0 mb-0">
                {categories.map((category) => {
                  const isExpanded = expandedCategories.includes(category.id);
                  return (
                    <li key={category.id} className="mb-1">
                      <div>
                        <button
                          onClick={() => handleCategoryClick(category)}
                          className={`flex items-center justify-between px-4 py-3 w-full text-left uppercase transition-all duration-200 rounded-xl font-medium text-sm
                            ${isExpanded ? 'bg-blue-50 text-brand-blue' : 'text-gray-700 hover:text-brand-blue hover:bg-blue-50'}
                          `}
                        >
                          <span className="font-bold">{category.name}</span>
                          <motion.div
                            animate={{ rotate: isExpanded ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <FontAwesomeIcon icon={faChevronRight} className="text-sm opacity-70" />
                          </motion.div>
                        </button>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.ul
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="pl-3 mt-1 space-y-1 overflow-hidden"
                            >
                              <li className="mb-1">
                                <Link
                                  to={`/categoria/${category.slug}`}
                                  onClick={onClose}
                                  className="flex items-center justify-between px-4 py-2 text-sm text-gray-600 no-underline hover:text-brand-blue hover:bg-blue-50 transition-colors duration-200 rounded-xl font-medium"
                                >
                                  TODOS LOS PRODUCTOS
                                </Link>
                              </li>

                              {(category.categories || category.sub_categories).map((subCategory) => {
                                const isSubExpanded = expandedSubCategories.includes(subCategory.id);
                                return (
                                  <li key={subCategory.id} className="mb-1">
                                    <div>
                                      <button
                                        onClick={() => handleSubCategoryClick(subCategory, category.slug)}
                                        className={`flex items-center justify-between px-4 py-2 w-full text-left text-sm transition-colors duration-200 rounded-xl font-medium
                                          ${isSubExpanded ? 'text-brand-blue bg-blue-50' : 'text-gray-700 hover:text-brand-blue hover:bg-blue-50'}
                                        `}
                                      >
                                        <span>{subCategory.name}</span>
                                        {subCategory.sub_categories && subCategory.sub_categories.length > 0 && (
                                          <motion.div
                                            animate={{ rotate: isSubExpanded ? 90 : 0 }}
                                            transition={{ duration: 0.2 }}
                                          >
                                            <FontAwesomeIcon icon={faChevronRight} className="text-xs opacity-70" />
                                          </motion.div>
                                        )}
                                      </button>

                                      <AnimatePresence>
                                        {isSubExpanded && subCategory.sub_categories && (
                                          <motion.ul
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="pl-3 mt-1 space-y-1 overflow-hidden"
                                          >
                                            <li className="mb-1">
                                              <Link
                                                to={`/categoria/${category.slug}/${subCategory.slug}`}
                                                onClick={onClose}
                                                className="flex items-center justify-between px-4 py-2 text-sm text-gray-600 no-underline hover:text-brand-blue hover:bg-blue-50 transition-colors duration-200 rounded-xl font-medium"
                                              >
                                                TODOS LOS PRODUCTOS
                                              </Link>
                                            </li>

                                            {subCategory.sub_categories.map((subSubCategory) => (
                                              <li key={subSubCategory.id} className="mb-1">
                                                <Link
                                                  to={`/categoria/${category.slug}/${subCategory.slug}/${subSubCategory.slug}`}
                                                  onClick={onClose}
                                                  className="block px-4 py-2 text-sm text-gray-600 no-underline hover:text-brand-blue hover:bg-blue-50 transition-colors duration-200 rounded-xl"
                                                >
                                                  {subSubCategory.name}
                                                </Link>
                                              </li>
                                            ))}
                                          </motion.ul>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  </li>
                                );
                              })}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SidebarCategories;
